import { LiveblocksProvider } from "@liveblocks/react";
import { useAppSelector } from "@store";
import { getVersionCreatorId } from "@store/story/selectors";
import type { ReactNode } from "react";
import {
  useHandleAuth,
  useResolveMentionSuggestions,
  useResolveRoom,
  useResolveUsers,
} from "../hooks";

/**
 * Mounted around the whole routed app (core/providers/protected-route.provider).
 *
 * `LiveblocksProvider` is rendered unconditionally on purpose. It only puts a
 * client on context — no socket is opened and `authEndpoint` is not called until
 * a `RoomProvider` joins a room, and joining stays gated on
 * `useCollaborationStatus` where the rooms are mounted.
 *
 * It used to be wrapped in `CollaborationEnabledProvider fallback={children}`,
 * which rendered bare `children` until `useGetOrgInfoQuery` resolved and then
 * swapped in `<Provider>{children}</Provider>`. React unmounts a subtree when
 * the component type at a position changes, so every authenticated route
 * remounted ~400ms into the session. That cost a duplicate `GET /api/pages/:id`
 * (the story-load guards in use-get-story are refs, wiped by the remount), plus
 * a duplicate `POST /collaboration/authorize` and `POST /hocuspocus-token` when
 * the room and Yjs providers mounted a second time. Keeping the element type at
 * this position stable is the whole point — do not re-introduce a conditional
 * that swaps what sits here.
 */
export const CollaborationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <Provider>{children}</Provider>;
};

export const Provider = ({ children }: { children: ReactNode }) => {
  const resolveRoomInfo = useResolveRoom();
  const handleResolveMentionSuggestions = useResolveMentionSuggestions();
  const resolveUsers = useResolveUsers();
  const versionCreatorId = useAppSelector(getVersionCreatorId);
  const handleAuth = useHandleAuth();
  return (
    <LiveblocksProvider
      throttle={16}
      largeMessageStrategy="experimental-fallback-to-http"
      resolveUsers={async ({ userIds }): Promise<any> =>
        resolveUsers({ userIds })
      }
      resolveMentionSuggestions={async ({ text }) =>
        handleResolveMentionSuggestions({ text, versionCreatorId })
      }
      authEndpoint={handleAuth}
      resolveRoomsInfo={async ({ roomIds }) => resolveRoomInfo({ roomIds })}
    >
      {children}
    </LiveblocksProvider>
  );
};
